import { useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to protect actions that require authentication
 * @param {Function} action - The action to protect
 * @param {Object} options - Configuration options
 * @param {string} options.fallbackUrl - URL to return to after login (defaults to current path)
 * @param {string} options.message - Custom message to show on the login page
 * @returns {[Function, boolean, Error]} Tuple containing the protected action handler, loading state, and error
 */
export const useProtectedAction = (action, options = {}) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const protectedAction = useCallback(async (...args) => {
    if (!currentUser) {
      const returnUrl = options.fallbackUrl || location.pathname;
      navigate('/signin', { 
        state: { 
          returnUrl,
          message: options.message || 'Please sign in or register to continue',
        }
      });
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const result = await Promise.resolve(action(...args));
      return result;
    } catch (err) {
      console.error('Protected action failed:', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentUser, navigate, location.pathname, options.fallbackUrl, options.message, action]);

  return [protectedAction, loading, error];
}; 